// Phase 3.9 (hh-z24) — RegulationDiffScreen (case-43).
//
// Shows the maintenance-regulation diff produced by
// POST /api/v1/equipment/{id}/schedule/diff — three sections: what to add,
// what to modify (period change), what to remove. Each entry has a checkbox;
// "Применить выбранные" applies every checked entry one by one through
// POST /api/v1/equipment/{id}/schedule/diff/apply.
//
// PHOTO STEP (hh-560, case-43) — сценарий предполагает уточнение регламента по
// фото инструкции. Реальный разбор фото в дельту требует vision-пайплайна
// агента (hh-qvs, отложено) — без него снимок обработать нечем. До hh-qvs
// экран сразу показывает дифф по каталогу, а фото-уточнение — неактивная
// плашка «Скоро» (PhotoSoonCard).
//
// SAFETY MARKER (aidepo-18/42) — safety-critical entries carry a "safety" chip
// and are PRE-CHECKED by default (they're the recommended changes), but the
// user keeps full control: every checkbox is toggleable, including a "remove"
// of a safety rule. Safety is a marker/recommendation, never a hard block —
// the contract is "the user may delete anything", consistent across the
// create screen, the card, and this diff flow.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Icon, Chip, Btn } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { equipment, humanizeError } from '../../api';
import type {
  ScheduleDiffResponse,
  ApplyScheduleDiffRequest,
} from '../../api/endpoints/equipment.js';

export interface RegulationDiffScreenProps {
  readonly router: RouterApi;
  readonly equipmentId: string;
}

/** Stable per-entry key + the apply request it expands to. */
interface DiffEntry {
  readonly key: string;
  readonly section: 'add' | 'modify' | 'remove';
  readonly title: string;
  readonly detail: string;
  readonly isSafetyCritical: boolean;
  readonly request: ApplyScheduleDiffRequest;
}

function monthsLabel(months: number): string {
  if (months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? '1 год' : `${years} г.`;
  }
  return `${months} мес.`;
}

function flatten(diff: ScheduleDiffResponse): readonly DiffEntry[] {
  const entries: DiffEntry[] = [];
  diff.add.forEach((a, i) => {
    entries.push({
      key: `add:${i}:${a.title}`,
      section: 'add',
      title: a.title,
      detail: `Период ${monthsLabel(a.periodMonths)}`,
      isSafetyCritical: a.isSafetyCritical,
      request: {
        op: 'add',
        title: a.title,
        periodMonths: a.periodMonths,
        isSafetyCritical: a.isSafetyCritical,
      },
    });
  });
  diff.modify.forEach((m, i) => {
    entries.push({
      key: `modify:${i}:${m.ruleId}`,
      section: 'modify',
      title: m.title,
      detail: `${monthsLabel(m.fromPeriodMonths)} → ${monthsLabel(m.toPeriodMonths)}`,
      isSafetyCritical: m.isSafetyCritical,
      request: { op: 'modify', ruleId: m.ruleId, title: m.title, periodMonths: m.toPeriodMonths },
    });
  });
  diff.remove.forEach((r, i) => {
    entries.push({
      key: `remove:${i}:${r.ruleId}`,
      section: 'remove',
      title: r.title,
      detail: 'Будет удалён из регламента',
      isSafetyCritical: r.isSafetyCritical,
      request: { op: 'remove', ruleId: r.ruleId },
    });
  });
  return entries;
}

const SECTION_LABEL: Record<DiffEntry['section'], string> = {
  add: 'Добавить',
  modify: 'Изменить срок',
  remove: 'Удалить',
};

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

type Status = 'loading' | 'ready' | 'error' | 'applied';

export function RegulationDiffScreen({
  router,
  equipmentId,
}: RegulationDiffScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [entries, setEntries] = useState<readonly DiffEntry[]>([]);
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    equipment
      .scheduleDiff(equipmentId)
      .then((res) => {
        if (cancelled) return;
        const flat = flatten(res);
        setEntries(flat);
        // Safety-critical entries are pre-checked as the recommended default,
        // but remain toggleable (aidepo-42).
        setChecked(new Set(flat.filter((e) => e.isSafetyCritical).map((e) => e.key)));
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [equipmentId]);

  const toggle = (entry: DiffEntry): void => {
    // Every entry is toggleable (aidepo-42); safety is a recommendation,
    // not a lock. Only block while an apply is in flight.
    if (applying) return;
    setChecked((set) => {
      const next = new Set(set);
      if (next.has(entry.key)) next.delete(entry.key);
      else next.add(entry.key);
      return next;
    });
  };

  const selected = useMemo(
    () => entries.filter((e) => checked.has(e.key)),
    [entries, checked],
  );

  const apply = (): void => {
    if (applying || selected.length === 0) return;
    setApplying(true);
    setError(null);
    // Apply sequentially so failures surface against a known order.
    selected
      .reduce<Promise<unknown>>(
        (chain, entry) => chain.then(() => equipment.applyScheduleDiff(equipmentId, entry.request)),
        Promise.resolve(),
      )
      .then(() => {
        setStatus('applied');
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setApplying(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="Изменения регламента"
        subtitle="Отметь, что применить"
        onBack={() => router.back()}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
        )}
        {status === 'error' && error !== null && <ErrorBanner message={error} />}

        {(status === 'ready' || status === 'applied') && <PhotoSoonCard />}

        {status === 'applied' && (
          <div
            style={{
              marginTop: 14,
              padding: '20px 14px',
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft2}`,
              borderRadius: 14,
              fontSize: 13.5,
              color: T.brandDeep,
              fontWeight: 650,
              textAlign: 'center',
            }}
          >
            Регламент обновлён — {selected.length} изм.
          </div>
        )}

        {(status === 'ready' || status === 'applied') && entries.length === 0 && (
          <div
            style={{
              marginTop: 14,
              padding: '20px 14px',
              background: T.surfaceAlt,
              border: `1px solid ${T.border}`,
              borderRadius: 14,
              fontSize: 13,
              color: T.ink3,
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            Регламент актуален — менять нечего.
          </div>
        )}

        {status === 'ready' && entries.length > 0 && (
          <>
            <div
              style={{
                marginTop: 4,
                marginBottom: 12,
                padding: 12,
                background: T.safetySoft,
                border: '1px solid #E8D7A8',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Icon name="shield" size={16} color={T.safety} />
              <div style={{ fontSize: 12.5, color: T.safety, fontWeight: 600, flex: 1 }}>
                Safety-пункты отмечены и предложены по умолчанию — снять отметку
                можно, решение за вами.
              </div>
            </div>

            <div
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              {entries.map((entry, i) => (
                <div key={entry.key}>
                  {i > 0 && <div style={{ height: 1, background: T.divider, marginLeft: 46 }} />}
                  <DiffRow
                    entry={entry}
                    checked={checked.has(entry.key)}
                    disabled={applying}
                    onToggle={() => toggle(entry)}
                  />
                </div>
              ))}
            </div>

            {error !== null && <ErrorBanner message={error} />}
          </>
        )}
      </div>

      {status === 'ready' && entries.length > 0 && (
        <div
          style={{
            padding: '8px 16px 16px',
            borderTop: `1px solid ${T.border}`,
            background: T.bg,
          }}
        >
          <Btn
            kind="primary"
            icon="check"
            onClick={apply}
            disabled={selected.length === 0 || applying}
            style={{ width: '100%' }}
          >
            {applying ? 'Применяю…' : `Применить выбранные (${selected.length})`}
          </Btn>
        </div>
      )}
    </div>
  );
}

/**
 * Disabled "instruction photo" affordance. case-43 wants the regulation
 * refined from a photo of the manual; that needs the agent vision pipeline
 * (hh-qvs, deferred), so the step is shown as "Скоро" and the screen falls
 * back to the catalog diff above.
 */
function PhotoSoonCard(): JSX.Element {
  return (
    <div
      title="Скоро"
      style={{
        marginTop: 4,
        marginBottom: 12,
        padding: 12,
        background: T.surface,
        border: `1.5px dashed ${T.borderStrong}`,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        opacity: 0.7,
        cursor: 'not-allowed',
      }}
    >
      <Icon name="camera" size={18} color={T.ink3} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13.5, fontWeight: 650, color: T.ink2 }}>
            Уточнить по фото инструкции
          </span>
          <Chip tone="neutral">Скоро</Chip>
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
          Пока показываем дифф по каталогу — разбор фото появится позже.
        </div>
      </div>
    </div>
  );
}

interface DiffRowProps {
  readonly entry: DiffEntry;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
}

function DiffRow({ entry, checked, disabled, onToggle }: DiffRowProps): JSX.Element {
  const isSafety = entry.isSafetyCritical;
  return (
    <div
      onClick={disabled ? undefined : onToggle}
      style={{
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <div
        role="checkbox"
        aria-checked={checked}
        aria-disabled={disabled}
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          marginTop: 1,
          flexShrink: 0,
          border: `2px solid ${checked ? T.brand : T.borderStrong}`,
          background: checked ? T.brand : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {checked && <Icon name="check" size={13} color="#fff" strokeWidth={2.5} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{entry.title}</span>
          <Chip tone={entry.section === 'remove' ? 'danger' : 'brand'}>
            {SECTION_LABEL[entry.section]}
          </Chip>
          {isSafety && (
            <Chip tone="safety" icon="shield">
              safety
            </Chip>
          )}
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 4 }}>{entry.detail}</div>
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { readonly message: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: T.dangerSoft,
        border: '1px solid #E9C7BF',
        borderRadius: 12,
        fontSize: 12.5,
        color: T.danger,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}
